import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["unit", "inputAddon", "className", "bsSize", "disabled", "onChange", "onValueChanged", "min", "max", "value", "step", "digitPrecision", "placeholder"];
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useAfterMount from '../../hooks/useAfterMount';
var DEFAULT_DIGIT_PRECISION = 3;
var DEFAULT_VALUE = 0;
var DEFAULT_STEP = 1;

// Note: even if limits are set and input type is number, many browsers allow to enter invalid data
// like entering characters or values outside the boundaries, hence we have to check the input here
// See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/number

export var defaultProps = {
  min: 0,
  max: Number.MAX_VALUE,
  step: DEFAULT_STEP
};
var getValueFromProps = function getValueFromProps(val, min, max, placeholder) {
  // Show placeholders if given instead of 0
  if (val === undefined && placeholder) {
    return '';
  }
  return clampNumber(Number(val), min, max) || DEFAULT_VALUE;
};
var clampNumber = function clampNumber(value, min, max) {
  if (value < min) {
    return min;
  } else if (value > max) {
    return max;
  }
  return value;
};
export var convertNonIntegerToDefault = function convertNonIntegerToDefault(value) {
  var currentValue = value;
  if (!Number.isFinite(currentValue)) {
    currentValue = DEFAULT_VALUE;
  }
  return currentValue;
};
var NumberInput = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var unit = props.unit,
    inputAddon = props.inputAddon,
    className = props.className,
    bsSize = props.bsSize,
    disabled = props.disabled,
    onChange = props.onChange,
    _props$onValueChanged = props.onValueChanged,
    onValueChanged = _props$onValueChanged === void 0 ? function () {} : _props$onValueChanged,
    propMin = props.min,
    propMax = props.max,
    propValue = props.value,
    _props$step = props.step,
    step = _props$step === void 0 ? DEFAULT_STEP : _props$step,
    _props$digitPrecision = props.digitPrecision,
    digitPrecision = _props$digitPrecision === void 0 ? DEFAULT_DIGIT_PRECISION : _props$digitPrecision,
    placeholder = props.placeholder,
    remainingProps = _objectWithoutProperties(props, _excluded);

  // Note, "onChange" should replace "onValueChanged" in the future but it's widely used
  var callback = onChange || onValueChanged;
  var min = Number.isFinite(propMin) ? propMin : defaultProps.min;
  var max = Number.isFinite(propMax) ? propMax : defaultProps.max;
  var value = getValueFromProps(propValue, min, max, placeholder);

  // Define local state and define initial values
  var _useState = useState({
      value: value,
      enteredValue: value,
      isValid: true
    }),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];

  // Update internal value whenever the value prop from outside changes
  useAfterMount(function () {
    setState({
      value: getValueFromProps(propValue, min, max, placeholder),
      enteredValue: value,
      isValid: true
    });
  }, [propValue, clampNumber]);
  var applyValue = function applyValue(newValue) {
    if (newValue === '-' || newValue === '') {
      setState({
        value: newValue,
        enteredValue: newValue,
        isValid: true
      });
      return;
    }
    var enteredValue = Number(newValue);
    var isValid = !isNaN(enteredValue) && enteredValue >= min && enteredValue <= max;
    var newValidValue = clampNumber(enteredValue, min, max);
    setState({
      value: newValidValue,
      enteredValue: enteredValue,
      isValid: isValid
    });

    // Only call back the caller for valid values
    isValid && callback(newValidValue);
  };
  var handleOnChange = function handleOnChange(event) {
    applyValue(event.target.value);
  };

  // Prevent entering exponent to avoide side effects
  // See https://bugzilla.mozilla.org/show_bug.cgi?id=1398528
  var handleKeyDown = function handleKeyDown(event) {
    if (event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  };
  var handleBlur = function handleBlur(event) {
    // When the value is removed, keep the input empty but trigger the outside callback
    // since the user has finished his input
    var lastEnteredValue = state.enteredValue;
    if (lastEnteredValue === '') {
      callback(lastEnteredValue);
      return;
    }

    // Otherwise, validate the input, round it according to digitPrecision,
    // and clamp the value if the entered value exeeds the limitations
    var convertedEnteredValue = convertNonIntegerToDefault(Number(lastEnteredValue));
    var validNumber = clampNumber(Number(convertedEnteredValue).toFixed(digitPrecision), min, max);
    applyValue(validNumber);
  };
  var inputGroupClassNames = classNames('input-group', bsSize === 'sm' && 'input-group-sm', bsSize === 'lg' && 'input-group-lg');
  var inputClassNames = classNames('form-control', 'no-controls', bsSize === 'sm' && 'input-sm', bsSize === 'lg' && 'input-lg', className);
  var input = /*#__PURE__*/React.createElement("input", _extends({}, remainingProps, {
    type: "number",
    step: step,
    min: min,
    max: max,
    value: state.isValid ? state.value : state.enteredValue,
    className: inputClassNames,
    disabled: disabled,
    onBlur: handleBlur,
    onChange: handleOnChange,
    onKeyDown: handleKeyDown,
    ref: ref,
    "aria-label": "number-input",
    placeholder: placeholder,
    onWheel: function onWheel(event) {
      return event.target.blur();
    }
  }));
  return unit || inputAddon ? /*#__PURE__*/React.createElement("div", {
    className: inputGroupClassNames
  }, inputAddon && /*#__PURE__*/React.createElement("span", {
    className: 'input-group-addon'
  }, /*#__PURE__*/React.createElement("span", {
    className: inputAddon
  })), input, unit && /*#__PURE__*/React.createElement("span", {
    className: 'input-group-addon'
  }, unit)) : input;
});
NumberInput.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  value: PropTypes.number,
  step: PropTypes.number,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onValueChanged: PropTypes.func,
  bsSize: PropTypes.oneOf(['sm', 'lg', 'small', 'large']),
  className: PropTypes.string,
  unit: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  inputAddon: PropTypes.string,
  digitPrecision: PropTypes.number,
  placeholder: PropTypes.string
};
export default NumberInput;